export interface NylasResponse<Data = any> {
  request_id: string;
  data?: Data;
  error?: {
    message: string;
    type: string;
  };
}

// Avaliability
export interface OpenHours {
  days: number[];
  exDates: string[];
  timezone: string;
  start: string;
  end: string;
}

export interface MeetingBuffer {
  before: number;
  after: number;
}

// Scheduling
export interface UpdateConfiguration extends Omit<Configuration, 'id'> {}

export interface ParticipantAvailability {
  calendar_ids: string[];
  open_hours?: OpenHours[];
}

export type NylasSchedulerBookingParticipant = { name: string; email: string; nameReadOnly?: boolean; emailReadOnly?: boolean };

export type NylasSchedulerBookingData = {
  primaryParticipant: NylasSchedulerBookingParticipant;
  startTime?: Date;
  endTime?: Date;
  timezone?: string;
  language?: string;
  guests?: NylasSchedulerBookingParticipant[];
  additionalFields?: Record<
    string,
    {
      value: string;
      type?: string;
      readOnly?: boolean;
    }
  >;
};

export type OpenHour = {
  days: number[]; // [1, 2, 3, 4, 5]
  start: string; // "09:00"
  end: string; // "17:00"
  timezone: string; // "America/Los_Angeles"
};

export type Timeslot = {
  start_time: Date;
  end_time: Date;
  emails?: string[];
  capacity?: number;
  event_id?: string;
  master_id?: string;
  calendar_id?: string;
};

export type ThemeConfig = Partial<{
  '--nylas-primary': string;
  '--nylas-info': string;
  '--nylas-success': string;
  '--nylas-warning': string;
  '--nylas-error': string;
  '--nylas-error-pressed': string;
  '--nylas-base-0': string;
  '--nylas-base-25': string;
  '--nylas-base-50': string;
  '--nylas-base-100': string;
  '--nylas-base-200': string;
  '--nylas-base-300': string;
  '--nylas-base-400': string;
  '--nylas-base-500': string;
  '--nylas-base-600': string;
  '--nylas-base-700': string;
  '--nylas-base-800': string;
  '--nylas-base-900': string;
  '--nylas-base-950': string;
  '--nylas-font-family': string;
  '--nylas-font-size': string;
  '--nylas-border-radius': string;
  '--nylas-border-radius-2x': string;
  '--nylas-border-radius-3x': string;
}>;

export enum NotificationType {
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
  Success = 'success',
}

export type Notification = {
  id: string;
  type: NotificationType;
  title: string;
  code?: number;
  category: string;
  description?: string;
  ttl?: number | 'none';
};

export type BookingType = 'booking' | 'organizer-confirmation' | 'custom-confirmation';

export type AvailabilityMethod = 'max-fairness' | 'max-availability' | 'collective';

export type ConferenceProvider = 'GoogleMeetConferenceProvider' | 'MicrosoftTeamsConferenceProvider' | 'ZoomConferenceProvider' | 'OnlineMeetingProviderMicrosoftTeams';

export type ReminderMethod = 'email' | 'webhook';

export interface Rules {
  availability_method: AvailabilityMethod;
  buffer: MeetingBuffer;
  default_open_hours: OpenHours[];
  round_robin_group_id: string;
}

export interface ConferenceDetail {
  meeting_code?: string; // zoom, GTM, microsoft teams
  password?: string; // zoom, webex
  url?: string; // zoom, webex, GTM, meet, microsoft teams
  pin?: string; // webex, meet
  phone?: string[]; // GTM, meet, microsoft teams
}

export interface Conference {
  provider?: ConferenceProvider;
  details?: ConferenceDetail;
  autocreate?: { [key: string]: any };
}

export interface ParticipantBooking {
  calendar_id: string;
}

export interface Participant {
  email: string;
  name?: string;
  is_organizer?: boolean;
  availability?: ParticipantAvailability;
  booking?: ParticipantBooking;
}

export interface EventReminder {
  type: ReminderMethod;
  minutes_before_event: number;
  recipient?: string;
  email_subject?: string;
}

export interface Availability {
  duration_minutes: number;
  interval_minutes?: number;
  round_to?: number;
  availability_rules?: Rules;
}

export interface EventBooking {
  title: string;
  description?: string;
  location?: string;
  timezone?: string;
  booking_type: BookingType;
  conferencing?: Conference;
  hide_participants?: boolean;
  disable_emails?: boolean;
  reminders?: EventReminder[];
}

export interface Appearance {
  color?: string;
  submit_button_label?: string;
  thank_you_message?: string;
  company_logo_url?: string;
}

export interface Scheduler {
  available_days_in_future?: number;
  min_cancellation_notice: number;
  min_booking_notice?: number;
  rescheduling_url?: string;
  cancellation_url?: string;
  organizer_confirmation_url?: string;
  cancellation_policy?: string;
  hide_additional_guests?: boolean;
  hide_cancellation_options?: boolean;
  hide_rescheduling_options?: boolean;
  additional_fields?: Record<string, AdditionalFields>;
  email_template?: EmailTemplate;
  confirmation_redirect_url?: string;
}
export interface AdditionalFields {
  type: string;
  required: boolean;
  order: number;
  options?: string[];
  label?: string;
  default?: string;
}

export interface EmailTemplate {
  logo: string;
  booking_confirmed: BookingConfirmed;
  show_nylas_branding: boolean;
  organizer_locale: string;
}

export interface BookingConfirmed {
  title: string;
  body: string;
}

export interface Configuration {
  id: string;
  version: string;
  type?: string;
  requires_session_auth?: boolean;
  participants: Participant[];
  availability: Availability;
  event_booking: EventBooking;
  scheduler: Partial<Scheduler>;
  slug: string;
  appearance: Appearance;
  name: string;
}

export interface GroupBooking {
  booking_type: BookingType; // Only 'booking' is supported
  disable_emails?: boolean;
  reminders?: EventReminder[];
  conferencing?: Conference;
  calendar_id: string;
  default_capacity: number;
}

export interface GroupConfiguration {
  id: string;
  name: string;
  slug: string;
  requires_session_auth?: boolean;
  type: 'group';
  group_booking: GroupBooking;
  scheduler: Scheduler;
  appearance: Appearance;
}

export interface Creator {
  name: string;
  email: string;
}
export interface AdditionalParticipant {
  name: string;
  email: string;
  calendars: {
    id: string;
    name: string;
    object?: 'calendar';
    is_primary?: boolean;
    is_owned_by_user?: boolean;
    read_only?: boolean;
    timezone?: string;
  }[];
}

export interface GroupEvent {
  id?: string;
  title: string;
  busy: boolean;
  participants: NylasSchedulerBookingParticipant[];
  resources: {
    name: string;
    email: string;
  }[];
  description: string;
  when: {
    start_time: number;
    end_time: number;
    start_timezone?: string;
    end_timezone?: string;
  };
  location: string;
  recurrence: string[];
  capacity: number;
}

export type RecurrenceUpdateOption = 'this' | 'future' | 'all';
export type RecurrenceDeleteOption = 'this' | 'future' | 'all';

export type Snapshot = {
  busy: boolean;
  calendar_id: string;
  hide_participants: boolean;
  ical_uid: string;
  organizer: {
    name: string;
    email: string;
  };
  participants: {
    email: string;
    status: string;
  }[];
  resources: any[];
  read_only: boolean;
  recurrence: string[];
  reminders: {
    use_default: boolean;
    overrides: any[];
  };
  title: string;
  description?: string;
  location?: string;
  conferencing?: Conference;
  visibility: string;
  creator: {
    name: string;
    email: string;
  };
  html_link: string;
  grant_id: string;
  id: string;
  object: string;
  status: string;
  when: {
    start_timezone: string;
    end_timezone: string;
    object: string;
    start_time: number; // Unix timestamp
    end_time: number; // Unix timestamp
  };
  created_at: number; // Unix timestamp
  updated_at: number; // Unix timestamp
};

export interface GroupEventAPIData {
  event: EventDetails;
  group_event_info?: GroupEventInfo;
}

export type EventDetails = {
  busy: boolean;
  calendar_id: string;
  conferencing: {
    provider: ConferenceProvider;
    details: {
      meeting_code: string;
      url: string;
      pin: string;
      phone: string[];
    };
  };
  hide_participants: boolean;
  ical_uid: string;
  organizer: {
    name: string;
    email: string;
  };
  participants: Array<{
    email: string;
    status: 'yes' | 'no' | 'noreply';
    comment?: string;
  }>;
  resources: string[];
  read_only: boolean;
  reminders: {
    use_default: boolean;
    overrides: Array<{
      method?: string;
      minutes?: number;
    }>;
  };
  title: string;
  description?: string;
  location?: string;
  visibility: 'default' | 'public' | 'private';
  creator: {
    name: string;
    email: string;
  };
  html_link: string;
  master_event_id: string;
  color_id: string;
  grant_id: string;
  id: string;
  object: 'event';
  status: 'confirmed' | 'tentative' | 'cancelled';
  when: {
    start_timezone: string;
    end_timezone: string;
    object: 'timespan';
    start_time: number;
    end_time: number;
  };
  created_at: number;
  updated_at: number;
  original_start_time: number;
};

export type GroupEventInfo = {
  event_id: string;
  calendar_id: string;
  master_id: string;
  config_id: string;
  capacity: number;
  type: 'updated_event' | 'single_event' | 'master_event';
  booked: boolean;
  finished: boolean;
  participants: Array<{
    email: string;
    is_organizer: boolean;
    name: string;
    timezone: string;
  }>;
  snapshot: any | null;
  snapshot_expires_at: number;
  rrule: string[] | null;
  exceptions: {
    event_id: string;
    capacity: number;
    participants: Array<{
      email: string;
      is_organizer: boolean;
      name: string;
      timezone: string;
    }>;
  }[];
};

export type ImportGroupEventDetails = {
  event_id: string;
  calendar_id: string;
  capacity: number;
  participants: Participant[];
  organizer: {
    name: string;
    email: string;
  };
};

export type ImportGroupEventResponse = {
  imported_events: string[];
  import_failed: {
    event_id: string;
    reason: string;
  }[];
};

export enum ErrorCategory {
  Component = 'component',
  Api = 'api',
  Auth = 'auth',
}

export interface ErrorDetails {
  title: string;
  message: string;
  category: ErrorCategory;
}

export enum ComponentErrorType {
  endtime_not_in_future = 'endtime_not_in_future',
  no_booking_info = 'no_booking_info',
  no_timeslot_selected = 'no_timeslot_selected',
  no_timezone_selected = 'no_timezone_selected',
  no_booking_id = 'no_booking_id',
  no_salt = 'no_salt',
  invalid_start_time = 'invalid_start_time',
  invalid_end_time = 'invalid_end_time',
  invalid_timezone = 'invalid_timezone',
  no_config_id = 'no_config_id',
}

export interface KeyPoint {
  time: number;
  text: string;
  active?: boolean;
}
